import React, { useState, useEffect } from 'react';
import BASE_URL, { SEO_BASE_URL } from '../config';
import {
  FaCheck, FaTimes, FaCompass,
  FaUsers, FaMagic, FaFileExport, FaCamera, FaReceipt,
  FaHeadset, FaStar, FaPlane, FaUser, FaBriefcase, FaGlobe,
  FaBuilding, FaRocket, FaTicketAlt, FaGift, FaTag
} from 'react-icons/fa';
import { createCheckoutSession, createPaygCheckout, getUpgradePreview, getSubscriptionTiers } from '../api/client';
import SEO from './SEO';
import InternalLinks from './InternalLinks';

const Pricing = ({ user, onBack, onUpgradeSuccess, onNavigate, onShowRegister }) => {
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [pricingTab, setPricingTab] = useState('consumer');
  const [upgradePreview, setUpgradePreview] = useState(null);
  const [upgradeTarget, setUpgradeTarget] = useState(null);
  const [promotions, setPromotions] = useState({});  // Map of tier_id -> promotion info
  const [promoSuccess, setPromoSuccess] = useState(null);

  // Fetch active promotions on mount
  useEffect(() => {
    const fetchPromotions = async () => {
      try {
        const data = await getSubscriptionTiers();
        const promoMap = {};
        // Extract promotions from consumer and commercial tiers
        [...(data.consumer || []), ...(data.commercial || [])].forEach(tier => {
          if (tier.promotion) {
            promoMap[tier.id] = tier.promotion;
          }
        });
        setPromotions(promoMap);
      } catch (err) {
        console.error('Error fetching promotions:', err);
      }
    };
    fetchPromotions();
  }, []);

  const isCurrentTier = (tierId) => user?.subscription_tier === tierId;

  const paidTiers = ['individual', 'group', 'starter_agent', 'professional_agent', 'enterprise_agency'];
  const isExistingSubscriber = user && paidTiers.includes(user.subscription_tier);

  // Tiers that can only be purchased on a yearly cycle, regardless of the toggle.
  const YEARLY_ONLY_TIERS = new Set(['individual']);
  const cycleForTier = (tierId) => (YEARLY_ONLY_TIERS.has(tierId) ? 'yearly' : billingCycle);

  const handleSubscribe = async (tierId) => {
    setError(null);

    // Redirect to registration with plan context if not logged in
    if (!user) {
      // Store pending plan in sessionStorage for post-registration checkout
      sessionStorage.setItem('pending_plan', JSON.stringify({
        type: 'subscription',
        tierId: tierId,
        billingCycle: cycleForTier(tierId)
      }));
      // Trigger registration flow
      if (onShowRegister) {
        onShowRegister();
      }
      return;
    }

    // If user has an active paid subscription, show upgrade preview first
    if (isExistingSubscriber) {
      setLoading(tierId);
      try {
        const preview = await getUpgradePreview(cycleForTier(tierId), tierId);
        setUpgradePreview(preview);
        setUpgradeTarget(tierId);
      } catch (err) {
        console.error('Error fetching upgrade preview:', err);
        setError(err.response?.data?.detail || 'Failed to preview upgrade. Please try again.');
      } finally {
        setLoading(null);
      }
      return;
    }

    // New subscriber — go to Stripe checkout
    setLoading(tierId);
    try {
      const result = await createCheckoutSession(cycleForTier(tierId), tierId);

      // Check if a 100% promotion was applied directly (no Stripe redirect)
      if (result.upgraded) {
        setPromoSuccess({
          tier: result.new_tier,
          message: result.message || 'Free promotion applied successfully!'
        });
        onUpgradeSuccess?.(result);
        setLoading(null);
        return;
      }

      if (result.checkout_url) {
        if (result.session_id) {
          sessionStorage.setItem('stripe_session_id', result.session_id);
        }
        window.location.href = result.checkout_url;
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError(err.response?.data?.detail || 'Failed to start checkout. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  const handleConfirmUpgrade = async () => {
    if (!upgradeTarget) return;
    setLoading(upgradeTarget);
    setError(null);

    try {
      const result = await createCheckoutSession(cycleForTier(upgradeTarget), upgradeTarget);
      if (result.upgraded) {
        setUpgradePreview(null);
        setUpgradeTarget(null);
        onUpgradeSuccess?.(result);
      } else if (result.checkout_url) {
        if (result.session_id) {
          sessionStorage.setItem('stripe_session_id', result.session_id);
        }
        window.location.href = result.checkout_url;
      }
    } catch (err) {
      console.error('Error upgrading subscription:', err);
      setError(err.response?.data?.detail || 'Failed to upgrade. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  const handlePayg = async (planType) => {
    setError(null);
    // Redirect to registration with plan context if not logged in
    if (!user) {
      // Store pending PAYG purchase in sessionStorage for post-registration checkout
      sessionStorage.setItem('pending_plan', JSON.stringify({
        type: 'payg',
        planType: planType
      }));
      // Trigger registration flow
      if (onShowRegister) {
        onShowRegister();
      }
      return;
    }
    setLoading(`payg_${planType}`);

    try {
      const result = await createPaygCheckout(planType);
      if (result.checkout_url) {
        if (result.session_id) {
          sessionStorage.setItem('stripe_session_id', result.session_id);
        }
        window.location.href = result.checkout_url;
      }
    } catch (err) {
      console.error('Error creating PAYG checkout:', err);
      setError(err.response?.data?.detail || 'Failed to start checkout. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  const consumerTiers = [
    {
      id: 'payg',
      name: 'Pay As You Go',
      tagline: 'No subscription, no commitment',
      icon: FaTicketAlt,
      color: 'from-secondary-50 to-secondary-100',
      textColor: 'text-secondary-700',
      borderColor: 'border-secondary-200',
      btnColor: 'from-secondary-400 to-secondary-500',
      isPayg: true,
      price: { perItinerary: 4.99 },
      features: [
        { text: 'Pay only when you plan a trip', included: true, icon: FaCheck, highlight: true },
        { text: 'AI-powered route planning', included: true, icon: FaMagic },
        { text: 'AI modifications', included: true, icon: FaMagic },
        { text: 'Export to PDF', included: true, icon: FaFileExport },
        { text: 'Itinerary photos & attachments', included: true, icon: FaCamera },
        { text: 'Credits never expire', included: true, icon: FaCheck },
      ],
      notIncluded: [
        { text: 'Group planning / invite members', icon: FaUsers },
        { text: 'Expense tracking & splitting', icon: FaReceipt },
        { text: 'Earn rewards via referrals', icon: FaGift },
      ],
    },
    {
      id: 'individual',
      name: 'Solo',
      tagline: 'For solo travelers',
      icon: FaUser,
      color: 'from-primary-50 to-primary-100',
      textColor: 'text-primary-700',
      borderColor: 'border-primary-200',
      btnColor: 'from-primary-400 to-primary-500',
      popular: true,
      price: { monthly: 0, yearly: 29.95 },
      yearlyOnly: true,
      features: [
        { text: 'Unlimited itineraries', included: true, icon: FaCheck, highlight: true },
        { text: 'AI-powered modifications', included: true, icon: FaMagic, highlight: true },
        { text: 'AI Chat assistant', included: true, icon: FaCheck, highlight: true },
        { text: 'Export to PDF', included: true, icon: FaFileExport },
        { text: 'Itinerary photos & attachments', included: true, icon: FaCamera },
        { text: 'Offline access', included: true, icon: FaCheck },
        { text: 'Earn rewards via referrals', included: true, icon: FaGift },
      ],
      notIncluded: [
        { text: 'Expense tracking & splitting', icon: FaReceipt },
        { text: 'Invite members / Group Chat', icon: FaUsers },
        { text: 'Receipt scanning', icon: FaCamera },
        { text: 'Priority support', icon: FaHeadset },
      ],
    },
    {
      id: 'group',
      name: 'Group',
      tagline: 'For friends & family',
      icon: FaUsers,
      color: 'from-accent-50 to-accent-100',
      textColor: 'text-accent-700',
      borderColor: 'border-accent-200',
      btnColor: 'from-accent-400 to-accent-500',
      price: { monthly: 14.99, yearly: 79.99 },
      savingsPercent: 56,
      features: [
        { text: 'Everything in Solo', included: true, icon: FaCheck },
        { text: 'Group planning (up to 6 members)', included: true, icon: FaUsers, highlight: true },
        { text: 'Expense tracking & splitting', included: true, icon: FaReceipt, highlight: true },
        { text: 'Group Chat', included: true, icon: FaCheck, highlight: true },
        { text: 'Receipt scanning', included: true, icon: FaCamera, highlight: true },
        { text: 'Priority support', included: true, icon: FaHeadset, highlight: true },
        { text: 'Earn rewards via referrals', included: true, icon: FaGift },
      ],
      notIncluded: [],
    },
  ];

  const commercialTiers = [
    {
      id: 'starter_agent',
      name: 'Starter',
      tagline: '',
      icon: FaBriefcase,
      color: 'from-adventure-50 to-adventure-100',
      textColor: 'text-adventure-700',
      borderColor: 'border-adventure-200',
      btnColor: 'from-adventure-400 to-adventure-500',
      price: { monthly: 29 },
      features: [
        { text: '10 itineraries/month (+$2/ea overage)', included: true, icon: FaCheck, highlight: true },
        { text: 'Group itinerary planning', included: true, icon: FaUsers },
        { text: 'AI modifications', included: true, icon: FaMagic },
        { text: 'Export to PDF', included: true, icon: FaFileExport },
        { text: 'Itinerary photos & attachments', included: true, icon: FaCamera },
        { text: 'Priority support', included: true, icon: FaHeadset },
      ],
      notIncluded: [],
    },
    {
      id: 'professional_agent',
      name: 'Professional',
      tagline: 'For growing agencies',
      icon: FaBuilding,
      color: 'from-golden-50 to-golden-100',
      textColor: 'text-golden-700',
      borderColor: 'border-golden-200',
      btnColor: 'from-golden-400 to-golden-500',
      popular: true,
      price: { monthly: 59 },
      features: [
        { text: '40 itineraries/month (+$1.50/ea overage)', included: true, icon: FaCheck, highlight: true },
        { text: 'Everything in Starter', included: true, icon: FaCheck },
        { text: 'Advanced analytics', included: true, icon: FaStar, highlight: true },
        { text: 'Dedicated support', included: true, icon: FaHeadset, highlight: true },
      ],
      notIncluded: [],
    },
    {
      id: 'enterprise_agency',
      name: 'Enterprise',
      tagline: 'For large operations',
      icon: FaGlobe,
      color: 'from-secondary-50 to-secondary-100',
      textColor: 'text-secondary-700',
      borderColor: 'border-secondary-200',
      btnColor: 'from-secondary-400 to-secondary-500',
      price: { monthly: 149 },
      features: [
        { text: 'Unlimited itineraries', included: true, icon: FaCheck, highlight: true },
        { text: 'Everything in Professional', included: true, icon: FaCheck },
        { text: 'White-label branding', included: true, icon: FaRocket, highlight: true },
        { text: 'Custom integrations', included: true, icon: FaStar, highlight: true },
        { text: 'Dedicated account manager', included: true, icon: FaHeadset, highlight: true },
      ],
      notIncluded: [],
    },
  ];

  const renderTierCard = (tier, isCommercial = false) => {
    const Icon = tier.icon;
    const isCurrent = isCurrentTier(tier.id);
    const promo = promotions[tier.id];
    const originalPrice = isCommercial
      ? tier.price.monthly
      : tier.yearlyOnly
        ? tier.price.yearly
        : billingCycle === 'yearly' && tier.price.yearly
          ? tier.price.yearly
          : tier.price.monthly;

    // Calculate discounted price if promotion exists
    let price = originalPrice;
    let hasPromo = false;
    if (promo) {
      hasPromo = true;
      if (promo.is_free) {
        price = 0;
      } else {
        price = Math.round(originalPrice * (1 - promo.discount_percentage / 100) * 100) / 100;
      }
    }

    const isYearly = !isCommercial && !tier.isPayg && (tier.yearlyOnly || billingCycle === 'yearly');
    const isFree = tier.id === 'free';
    const isPayg = !!tier.isPayg;
    const showSavings = isYearly && tier.savingsPercent && !hasPromo;

    return (
      <div
        key={tier.id}
        className={`relative bg-white rounded-2xl overflow-hidden border-2 flex flex-col ${
          tier.popular ? 'border-primary-400 ring-4 ring-primary-100' : tier.borderColor
        } ${isCurrent ? 'ring-2 ring-green-500 ring-offset-2' : ''} transition-transform hover:scale-[1.02]`}
        style={{ boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12), 0 4px 10px rgba(0, 0, 0, 0.08)' }}
      >
        {/* Promotion Badge */}
        {hasPromo && (
          <div className="absolute top-0 right-0">
            <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold px-4 py-1.5 rounded-bl-xl flex items-center gap-1">
              <FaTag className="text-[10px]" />
              {promo.is_free ? 'FREE!' : `${promo.discount_percentage}% OFF`}
            </div>
          </div>
        )}

        {/* Popular Badge (only show if no promotion) */}
        {tier.popular && !hasPromo && (
          <div className="absolute top-0 right-0">
            <div className="bg-gradient-to-r from-primary-500 to-primary-600 text-white text-xs font-bold px-4 py-1.5 rounded-bl-xl">
              MOST POPULAR
            </div>
          </div>
        )}

        {/* Savings Badge (only show if no promotion) */}
        {showSavings && (
          <div className="absolute top-0 left-0">
            <div className="bg-green-500 text-white text-xs font-bold px-4 py-1.5 rounded-br-xl">
              Save {tier.savingsPercent}%
            </div>
          </div>
        )}

        {/* Current Plan Badge */}
        {isCurrent && !tier.popular && !showSavings && (
          <div className="absolute top-0 left-0">
            <div className={`${user?.subscription_status === 'cancelling' ? 'bg-amber-500' : 'bg-green-500'} text-white text-xs font-bold px-4 py-1.5 rounded-br-xl`}>
              {user?.subscription_status === 'cancelling' ? 'CANCELLING' : 'YOUR PLAN'}
            </div>
          </div>
        )}

        {/* Card Header */}
        <div className={`p-6 bg-gradient-to-r ${tier.color} ${tier.textColor}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-white/60 rounded-xl flex items-center justify-center flex-shrink-0">
              <Icon className="text-xl" />
            </div>
            <div className="min-w-0">
              <h3 className="text-xl font-bold whitespace-nowrap">{tier.name}</h3>
              <p className="opacity-70 text-sm">{tier.tagline}</p>
            </div>
          </div>

          <div className="mt-4">
            {isPayg ? (
              <>
                <span className="text-4xl font-bold">${tier.price.perItinerary}</span>
                <span className="opacity-70 text-lg ml-1">/itinerary</span>
                <div className="mt-1 text-xs italic text-neutral-600">No subscription, pay per trip</div>
              </>
            ) : isFree ? (
              <>
                <span className="text-4xl font-bold">Free</span>
                <span className="opacity-70 text-lg ml-2">Forever</span>
              </>
            ) : hasPromo && promo.is_free ? (
              <>
                <span className="text-lg line-through opacity-50">${originalPrice}{isYearly ? '/yr' : '/mo'}</span>
                <span className="text-4xl font-bold text-green-600 ml-2">FREE</span>
                <div className="mt-1 text-sm text-yellow-700 bg-yellow-100 px-2 py-1 rounded">
                  Ends {new Date(promo.end_date).toLocaleDateString()}
                </div>
              </>
            ) : (
              <>
                {hasPromo && (
                  <span className="text-lg line-through opacity-50 mr-2">${originalPrice}{isYearly ? '/yr' : '/mo'}</span>
                )}
                <span className={`text-4xl font-bold ${hasPromo ? 'text-green-600' : ''}`}>${price}</span>
                <span className="opacity-70 text-lg ml-1">
                  {isYearly ? '/yr' : '/mo'}
                </span>
                {hasPromo && (
                  <div className="mt-1 text-sm text-yellow-700 bg-yellow-100 px-2 py-1 rounded inline-block">
                    Ends {new Date(promo.end_date).toLocaleDateString()}
                  </div>
                )}
                {!hasPromo && (
                  <div className="mt-1">
                    <span className="text-xs italic text-neutral-600">Just </span>
                    <span className="text-sm italic font-bold text-green-600">
                      ${(isYearly ? (price / 365) : (price / 30)).toFixed(2)}
                    </span>
                    <span className="text-xs italic text-neutral-600"> per day</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Features */}
        <div className="p-6 flex flex-col flex-grow">
          <ul className="space-y-3 flex-grow">
            {tier.features.map((feature, idx) => {
              const FeatureIcon = feature.icon;
              return (
                <li key={idx} className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    feature.highlight
                      ? 'bg-primary-100 text-primary-600'
                      : 'bg-green-100 text-green-600'
                  }`}>
                    <FeatureIcon className="text-xs" />
                  </div>
                  <span className={`text-sm ${feature.highlight ? 'font-semibold text-neutral-900' : 'text-neutral-700'}`}>
                    {feature.text}
                  </span>
                </li>
              );
            })}

            {tier.notIncluded.map((feature, idx) => (
              <li key={`not-${idx}`} className="flex items-start gap-3 opacity-50">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-neutral-100 text-neutral-400">
                  <FaTimes className="text-xs" />
                </div>
                <span className="text-sm text-neutral-500 line-through">{feature.text}</span>
              </li>
            ))}
          </ul>

          {/* CTA Button */}
          <div className="mt-6">
            {isPayg ? (
              <button
                onClick={() => handlePayg('individual')}
                disabled={loading === 'payg_individual'}
                className={`w-full py-3 px-4 rounded-xl font-bold text-center text-lg transition-all bg-gradient-to-r ${tier.btnColor} text-white shadow-lg hover:shadow-xl hover:scale-105 ${
                  loading === 'payg_individual' ? 'opacity-75 cursor-wait' : ''
                }`}
              >
                {loading === 'payg_individual' ? 'Processing...' : 'Buy 1 Credit'}
              </button>
            ) : isFree ? (
              <button
                disabled
                className="w-full py-3 px-4 rounded-xl font-semibold text-center bg-neutral-100 text-neutral-500 cursor-not-allowed"
              >
                {isCurrent ? 'Current Plan' : 'Free Plan'}
              </button>
            ) : isCurrent ? (
              <button
                disabled
                className={`w-full py-3 px-4 rounded-xl font-semibold text-center cursor-not-allowed ${
                  user?.subscription_status === 'cancelling'
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-green-100 text-green-700'
                }`}
              >
                {user?.subscription_status === 'cancelling' ? 'Cancelling' : 'Current Plan'}
              </button>
            ) : (
              <button
                onClick={() => handleSubscribe(tier.id)}
                disabled={loading === tier.id}
                className={`w-full py-3 px-4 rounded-xl font-bold text-center text-lg transition-all bg-gradient-to-r ${tier.btnColor} text-white shadow-lg hover:shadow-xl hover:scale-105 ${
                  loading === tier.id ? 'opacity-75 cursor-wait' : ''
                }`}
              >
                {loading === tier.id ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Processing...
                  </span>
                ) : hasPromo && promo.is_free ? (
                  `Activate Free ${tier.name}`
                ) : isExistingSubscriber ? (
                  `Switch to ${tier.name}`
                ) : (
                  `Get ${tier.name}`
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <SEO
        title="Pricing Plans"
        description="Choose the perfect ItinerarySeek plan for your travel needs. Pay-as-you-go, Solo, Group, and Commercial plans available with AI-powered itinerary generation."
        canonical={`${SEO_BASE_URL}/pricing`}
      />
      <div className="max-w-7xl mx-auto px-4 py-4">
        <button
          onClick={onBack}
          className="flex items-center space-x-2 text-neutral-700 hover:text-primary-600 font-medium transition-colors mb-3"
        >
          <span>&larr;</span>
          <span>Back</span>
        </button>

        {/* Header */}
        <div className="text-center mb-4">
          <h1 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-1">
            Choose Your Plan
          </h1>
          <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
            From solo travelers to travel agencies, we have a plan that fits your needs.
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="sticky top-2 z-40 max-w-md mx-auto mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-center shadow-lg">
            {error}
            <button onClick={() => setError(null)} className="ml-3 text-red-400 hover:text-red-600 font-bold">&times;</button>
          </div>
        )}

        {/* Plans + FAQ Side-by-Side */}
        <div className="flex flex-col lg:flex-row gap-6 max-w-7xl mx-auto mb-8">
          {/* Left: Tabs + Cards */}
          <div className="lg:w-3/4">
            {/* Tab Bar */}
            <div className="bg-neutral-200 px-2 pt-2 rounded-t-xl">
              <div className="flex items-center">
                <button
                  onClick={() => setPricingTab('consumer')}
                  className={`flex items-center justify-center gap-2 px-6 py-3 font-semibold text-sm md:text-base transition-all ${
                    pricingTab === 'consumer'
                      ? 'text-primary-600 bg-white rounded-t-lg border-t-4 border-primary-500'
                      : 'text-neutral-600 hover:text-primary-600'
                  }`}
                >
                  <FaUser className="text-lg" />
                  <span>Consumer Plans</span>
                </button>
                <button
                  onClick={() => setPricingTab('commercial')}
                  className={`flex items-center justify-center gap-2 px-6 py-3 font-semibold text-sm md:text-base transition-all ${
                    pricingTab === 'commercial'
                      ? 'text-secondary-600 bg-white rounded-t-lg border-t-4 border-secondary-500'
                      : 'text-neutral-600 hover:text-secondary-600'
                  }`}
                >
                  <FaBriefcase className="text-lg" />
                  <span>Commercial Plans</span>
                </button>
              </div>
            </div>

            {/* Tab Content */}
            <div className="bg-white rounded-b-xl px-6 py-4" style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)' }}>
              {pricingTab === 'consumer' && (
                <>
                  {/* Billing Toggle */}
                  <div className="flex items-center justify-center gap-4 mb-4">
                    <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-neutral-900' : 'text-neutral-500'}`}>
                      Monthly
                    </span>
                    <button
                      onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
                      className={`relative w-14 h-7 rounded-full transition-colors ${
                        billingCycle === 'yearly' ? 'bg-green-500' : 'bg-neutral-300'
                      }`}
                    >
                      <div className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${
                        billingCycle === 'yearly' ? 'translate-x-7' : 'translate-x-0.5'
                      }`} />
                    </button>
                    <span className={`text-sm font-medium ${billingCycle === 'yearly' ? 'text-neutral-900' : 'text-neutral-500'}`}>
                      Yearly
                    </span>
                    {billingCycle === 'yearly' && (
                      <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded-full">
                        Save up to 56%
                      </span>
                    )}
                  </div>

                  <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                    {consumerTiers.map(tier => renderTierCard(tier))}
                  </div>

                  {/* Auto-charge notice for subscription plans */}
                  <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-center">
                    <p className="text-sm text-amber-800">
                      <span className="font-semibold">Auto-renewal notice:</span> Solo plans are automatically charged annually, and Group plans are charged {billingCycle === 'yearly' ? 'annually' : 'monthly'} from the date of signup.
                      You can cancel anytime by submitting a request via our{' '}
                      <a href="/contact" className="text-primary-600 underline hover:text-primary-800 font-medium">Contact Us</a> page.
                      Your access continues until the end of your billing period.
                    </p>
                  </div>

                </>
              )}

              {pricingTab === 'commercial' && (
                <div className="py-12 text-center">
                  <div className="relative inline-block mb-6">
                    <div className="absolute -inset-4 bg-gradient-to-r from-secondary-200 via-primary-200 to-accent-200 rounded-full blur-xl opacity-60 animate-pulse"></div>
                    <div className="relative inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-secondary-400 to-secondary-600 rounded-2xl shadow-xl">
                      <FaRocket className="text-4xl text-white" />
                    </div>
                  </div>
                  <h3 className="text-3xl font-bold text-neutral-900 mb-3">
                    Something Amazing is Coming!
                  </h3>
                  <p className="text-lg text-neutral-600 max-w-lg mx-auto mb-6">
                    We're crafting powerful tools designed specifically for travel professionals.
                    Get ready for bulk itinerary generation, white-label branding, team collaboration,
                    and advanced analytics to supercharge your travel business.
                  </p>
                  <div className="flex flex-wrap justify-center gap-4 mb-8">
                    <div className="flex items-center gap-2 bg-gradient-to-r from-primary-50 to-primary-100 px-4 py-2 rounded-full">
                      <FaStar className="text-primary-500" />
                      <span className="text-sm font-medium text-primary-700">Bulk Generation</span>
                    </div>
                    <div className="flex items-center gap-2 bg-gradient-to-r from-secondary-50 to-secondary-100 px-4 py-2 rounded-full">
                      <FaGlobe className="text-secondary-500" />
                      <span className="text-sm font-medium text-secondary-700">White-Label</span>
                    </div>
                    <div className="flex items-center gap-2 bg-gradient-to-r from-accent-50 to-accent-100 px-4 py-2 rounded-full">
                      <FaUsers className="text-accent-500" />
                      <span className="text-sm font-medium text-accent-700">Team Tools</span>
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-secondary-50 via-primary-50 to-accent-50 rounded-2xl p-6 max-w-md mx-auto">
                    <p className="text-neutral-700 mb-4">
                      <span className="font-semibold">Be the first to know!</span> Join our waitlist for exclusive early access and special launch pricing.
                    </p>
                    <a
                      href="/contact"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-secondary-500 to-secondary-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all"
                    >
                      <FaRocket className="text-sm" />
                      Get Early Access
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: FAQ Sidebar */}
          <div className="lg:w-1/4">
            <h2 className="text-lg font-bold text-neutral-900 mb-3">FAQ</h2>
            <div className="space-y-3">
              <div className="bg-white rounded-xl overflow-hidden" style={{ boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)' }}>
                <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-4 py-2.5">
                  <h3 className="font-semibold text-white text-sm">What is pay-as-you-go?</h3>
                </div>
                <div className="px-4 py-3">
                  <p className="text-neutral-600 text-xs leading-relaxed">Pay-as-you-go lets you purchase individual itinerary credits without a subscription at $4.99 per itinerary.</p>
                </div>
              </div>
              <div className="bg-white rounded-xl overflow-hidden" style={{ boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)' }}>
                <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-4 py-2.5">
                  <h3 className="font-semibold text-white text-sm">Can I switch plans?</h3>
                </div>
                <div className="px-4 py-3">
                  <p className="text-neutral-600 text-xs leading-relaxed">Yes! Upgrade or downgrade anytime. Upgrades are prorated; downgrades take effect at the end of your billing period.</p>
                </div>
              </div>
              <div className="bg-white rounded-xl overflow-hidden" style={{ boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)' }}>
                <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-4 py-2.5">
                  <h3 className="font-semibold text-white text-sm">How does Group work?</h3>
                </div>
                <div className="px-4 py-3">
                  <p className="text-neutral-600 text-xs leading-relaxed">Up to 6 members (including owner) get unlimited itineraries, shared expenses feature, and collaborative planning.</p>
                </div>
              </div>
              <div className="bg-white rounded-xl overflow-hidden" style={{ boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)' }}>
                <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-4 py-2.5">
                  <h3 className="font-semibold text-white text-sm">What if I cancel?</h3>
                </div>
                <div className="px-4 py-3">
                  <p className="text-neutral-600 text-xs leading-relaxed">You keep access until your billing period ends, then move to Free. Your itineraries and data are preserved.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="flex flex-wrap items-center justify-center gap-6 text-neutral-400">
          <div className="flex items-center gap-2">
            <FaStar className="text-yellow-500" />
            <span className="text-sm">Secure Payments</span>
          </div>
          <div className="flex items-center gap-2">
            <FaPlane className="text-primary-500" />
            <span className="text-sm">AI-Powered Planning</span>
          </div>
          <div className="flex items-center gap-2">
            <FaHeadset className="text-secondary-500" />
            <span className="text-sm">Priority Support</span>
          </div>
        </div>

        {/* Internal Links for SEO */}
        <div className="mt-8">
          <InternalLinks currentPage="pricing" onNavigate={onNavigate} variant="compact" />
        </div>
      </div>

      {/* Promotion Success Modal */}
      {promoSuccess && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 text-center" style={{ boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)' }}>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaCheck className="text-3xl text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-neutral-900 mb-2">Promotion Applied!</h3>
            <p className="text-neutral-600 mb-6">{promoSuccess.message}</p>
            <button
              onClick={() => {
                setPromoSuccess(null);
                onBack?.();
              }}
              className="w-full py-3 px-4 rounded-xl font-bold text-center bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg hover:shadow-xl transition-all"
            >
              Start Planning
            </button>
          </div>
        </div>
      )}

      {/* Upgrade Confirmation Modal */}
      {upgradePreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6" style={{ boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)' }}>
            <h3 className="text-xl font-bold text-neutral-900 mb-4">Confirm Plan Change</h3>

            <div className="space-y-3 mb-6">
              <div className="flex justify-between text-sm">
                <span className="text-neutral-600">Current plan</span>
                <span className="font-semibold capitalize">{upgradePreview.current_tier.replace('_', ' ')}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-neutral-600">New plan</span>
                <span className="font-semibold capitalize">{upgradePreview.new_tier.replace('_', ' ')}</span>
              </div>
              <div className="border-t border-neutral-200 pt-3">
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600">Remaining days in period</span>
                  <span>{upgradePreview.remaining_days} days</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-neutral-600">Credit for unused time</span>
                  <span className="text-green-600">-${upgradePreview.credit_amount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-neutral-600">Prorated charge (new plan)</span>
                  <span>${upgradePreview.prorated_charge.toFixed(2)}</span>
                </div>
              </div>
              <div className="border-t border-neutral-200 pt-3">
                <div className="flex justify-between text-base font-bold">
                  <span>Due today</span>
                  <span className="text-primary-600">${upgradePreview.immediate_amount.toFixed(2)}</span>
                </div>
                <p className="text-xs text-neutral-500 mt-1">
                  Starting next billing cycle: ${upgradePreview.new_plan_price.toFixed(2)}/{upgradePreview.new_billing_cycle === 'yearly' ? 'year' : 'mo'}
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => { setUpgradePreview(null); setUpgradeTarget(null); }}
                className="flex-1 py-3 px-4 rounded-xl font-semibold text-center border border-neutral-300 text-neutral-700 hover:bg-neutral-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmUpgrade}
                disabled={loading === upgradeTarget}
                className="flex-1 py-3 px-4 rounded-xl font-bold text-center bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg hover:shadow-xl transition-all"
              >
                {loading === upgradeTarget ? 'Processing...' : 'Confirm Change'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Pricing;
